import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import React from 'react';
import { useForm } from 'react-hook-form';
import useMyMApi from '../../hooks/useMyMApi';
import useValidationRules from '../../hooks/useValidationRules';
import useWebContext from '../../hooks/useWebContext';
import { MyMButton, MyMSearchButton } from '../../ui-elements/MyMButton';
import { MyMDropDownList } from '../../ui-elements/MyMDropDownList';
import { MyMLabel, MyMVariantLabel } from '../../ui-elements/MyMLabel';
import ValidatableTextBox from '../../ui-parts/ValidatableTextBox';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**  
 * MyMine Version1.5 UserListフィルタ・ページング対応
 * UserListSearchCondition Component
 */
export const UserListSearchCondition = React.memo((props) => {
  const { formState, control, handleSubmit, reset } = useForm({
    mode: 'onSubmit',
  })
  const validationRules = useValidationRules();
  const {
    screenId,
    searchCondition,
    searchInputCondition,
    setSearchInputCondition,
    setPageNumber,
    setSortModel,
    setIsSearch,
  } = props
  const { controlSettings, messageList } = useWebContext(screenId);
  const apiRequest = useMyMApi(screenId);

  // データ取得時用の取得可能最大件数
  const limitMaxRowCount = 999;

  const handleForm = e => {
    setSearchInputCondition({
      ...searchInputCondition,
      [e.target.name]: e.target.value
    });
  }

  /** 検索クリック時処理 */
  const handleSearch = () => {
    // ページ番号・ソート情報をリセットし、useEffectフックで検索実施
    setIsSearch(true);
    setPageNumber(1);
    setSortModel([]);
  }

  /** クリアクリック時処理 */
  const handleClear = () => {
    reset();
    setSearchInputCondition({
      userId: "",
      userName: "",
      companyId: "",
      roleId: "",
      statusId: "",
    });
  };

  /** ダウンロードクリック時処理*/
  const handleDownload = async (e, format) => {

  /**
   * ユーザ情報取得成功コールバック処理
   * @param {any} result
   */
    const callbackUserList = (result) => {

      // [USER_SEQUENCE_NO]昇順にしてソート
      const sortedList = result.Result.ItemList.sort((a, b) => a.UserSequenceNo - b.UserSequenceNo);

      // 出力した時間を記録する
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const date = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');

      const exportedDate = `${year}${month}${date}${hours}${minutes}${seconds}`;
      {
        // workbookを作成
        const wb = XLSX.utils.book_new();

        // シートデータを作成
        const ws = XLSX.utils.json_to_sheet(sortedList);

        // workbookにシートを追加
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

        // Workbookをバイナリ書き出し
        const wbout = XLSX.write(wb, {
          bookType: 'xlsx',
          type: 'array',
        });

        // ダウンロードをトリガー
        saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `MyMUserList_${exportedDate}.xlsx`)
      }
    }

   /**
    * ユーザ情報取得失敗コールバック処理 
    */
   const callbackFailed = (result) => {
     result.Result = [];
   }

    // ユーザーの全件データをリクエスト
    await apiRequest.requestJson(`api/user-info/load-list?LimitMaxRowCount=${limitMaxRowCount}`, 'GET', callbackUserList, callbackFailed);
  }

  const dropdownDefaultProps = {
    sx: { minWidth: '45%' },
    width: "200px",
    needsBlank: true,
    onChange: handleForm,
  }

  const conditionControlList = [
    {
      lg: 3.5,
      label: controlSettings.LabelList.lblUserIdLabel,
      control:
        <ValidatableTextBox
          control={control}
          name="userId"
          defaultValue={searchInputCondition.userId}
          formState={formState}
          maxLength={256}
          rules={{
            ...validationRules.maxLength(256),
          }}
          onBlur={e => handleForm(e)}
        />
    },
    {
      lg: 3.5,
      label: controlSettings.LabelList.lblNameLabel,
      control:
        <ValidatableTextBox
          control={control}
          name="userName"
          defaultValue={searchInputCondition.userName}
          formState={formState}
          maxLength={100}
          rules={{
            ...validationRules.maxLength(100),
          }}
          onBlur={e => handleForm(e)}
        />
    },
    {
      lg: 3.5,
      label: controlSettings.LabelList.lblCompanyLabel,
      control: <MyMDropDownList {...dropdownDefaultProps} options={searchCondition.CompanyList} name="companyId" value={searchInputCondition.companyId} />
    },
    {
      lg: 3.5,
      label: controlSettings.LabelList.lblRoleLabel,
      control: <MyMDropDownList {...dropdownDefaultProps} options={searchCondition.RoleList} name="roleId" value={searchInputCondition.roleId} />
    },
    {
      lg: 3.5,
      label: controlSettings.LabelList.lblStatusLabel,
      control: <MyMDropDownList {...dropdownDefaultProps} options={searchCondition.StatusList} name="statusId" value={searchInputCondition.statusId} />
    },
  ];

  const onError = () => {
    window.alert(messageList.MyM_MSG_E_0003);
  };

  return (
    <Box sx={{ borderBottom: "3px solid #333", padding: "15px 20px", margin: "0px 20px" }}>
      <MyMVariantLabel variant="h6">{controlSettings.LabelList.lblUserList}</MyMVariantLabel>
      <form>
        <Grid container className='pt-2'>
          <Grid item xs={10}>
            <Grid container columnSpacing={5}>
              {conditionControlList.map((x, i) => {
                return (
                  <Grid item xs={12} md={x.md ?? 6} lg={x.lg} className='d-flex pb-2' key={i} sx={{ marginBottom: "7px" }}>
                    <MyMLabel sx={{ minWidth: 80, lineHeight: "40px" }}>
                      {x.label}
                    </MyMLabel>
                    {x.control}
                  </Grid>
                )
              })}
            </Grid>
          </Grid>
          <Grid item xs={2} className='d-flex align-items-end justify-content-end pb-2'>
            <Stack direction='row' marginRight='1rem' spacing={3}>
              <MyMSearchButton handleSearch={handleSubmit(handleSearch, onError)} searchText={controlSettings.LabelList.btnSearch} />
              <MyMButton onClick={handleClear}>{controlSettings.LabelList.btnClear}</MyMButton>
              <MyMButton onClick={handleDownload}>{controlSettings.LabelList.btnDownload}</MyMButton>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box >
  )
}
)
